import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { toastr } from 'react-redux-toastr';
import omit from 'lodash/omit';
import useIntl from '../../hooks/useIntl';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import useProspectAssignees from '../../hooks/data-fetching/useProspectAssignees';
import useActivityTypes from '../../hooks/data-fetching/useActivityTypes';
import useHasPermission from '../../hooks/useHasPermission';
import LeadManagementService from '../../services/leadManagementService';
import ProspectProfileService from '../../services/prospectProfileService';
import ActivityService from '../../services/activityService';
import { editCompletedActivity } from '../../components/EditCompletedActivityModal';
import { editPendingActivity } from '../../components/EditPendingActivityModal';
import store from '../../store';
import messages from './messages';

const service = new LeadManagementService();

// How many leads to reveal per "Load more". We grow a single page's size rather
// than accumulating separate page responses so the query result is always the
// authoritative list for the current stage — no client-side merge, no risk of a
// refetch double-listing a lead or leaking one stage's leads into another.
const PAGE_SIZE = 25;

// Group the active stages for the funnel's grouped, INVERTED display. Groups are
// ordered by their furthest stage (so 'closing' sits on top, 'intro' at the
// bottom) and stages within a group run furthest-first — all derived from the
// API's `order`/`group`, so no stage name or group order is hardcoded here.
const groupForInvertedDisplay = (stages) => {
  const byGroup = new Map();
  stages.forEach((stage) => {
    const list = byGroup.get(stage.group) ?? [];
    list.push(stage);
    byGroup.set(stage.group, list);
  });

  return Array.from(byGroup.entries())
    .map(([group, list]) => ({
      group,
      stages: [...list].sort((a, b) => b.order - a.order),
      maxOrder: list.reduce((max, s) => Math.max(max, s.order), 0),
    }))
    .sort((a, b) => b.maxOrder - a.maxOrder)
    .map(({ group, stages: groupStages }) => ({ group, stages: groupStages }));
};

export const useLeadManagement = () => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId;
  const propertyId = selectedProperty?.id;
  const propertyName = selectedProperty?.name ?? '';
  const enabled = Boolean(organizationId && propertyId);

  const canAssign = useHasPermission('prospect-assign');
  const { prospectAssignees } = useProspectAssignees();
  const { activityTypes } = useActivityTypes();

  // The activity control matches the prospects table: it needs read + update +
  // delete to be interactive (the modal can do all three), else it's read-only.
  const hasActivityReadPermission = useHasPermission('activity-read');
  const hasActivityUpdatePermission = useHasPermission('activity-update');
  const hasActivityDeletePermission = useHasPermission('activity-delete');
  const isActivityEditDisabled =
    (hasActivityReadPermission &&
      hasActivityUpdatePermission &&
      hasActivityDeletePermission) === false;

  const [selectedStageKey, setSelectedStageKey] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [assigningLeadId, setAssigningLeadId] = useState(null);

  // Reset the drill-down when the property context changes, so a stage selected
  // for one property never carries its open panel over to another.
  useEffect(() => {
    setSelectedStageKey(null);
    setVisibleCount(PAGE_SIZE);
  }, [organizationId, propertyId]);

  // Select a stage and collapse back to the first page in the SAME update, so
  // the leads query never fires once with the previous stage's larger "Load
  // more" size before an effect could reset it (avoids a transient over-fetch).
  const handleSelectStage = (stageKey) => {
    setSelectedStageKey(stageKey);
    if (stageKey !== selectedStageKey) {
      setVisibleCount(PAGE_SIZE);
    }
  };

  const stagesQuery = useQuery(
    ['leadManagementStages', organizationId, propertyId],
    () => service.getStages(organizationId, propertyId),
    { enabled, staleTime: 5 * 60 * 1000 },
  );

  const countsQuery = useQuery(
    ['leadManagementCounts', organizationId, propertyId],
    () => service.getCounts(organizationId, propertyId),
    { enabled, staleTime: 60 * 1000, refetchOnWindowFocus: true },
  );

  const leadsQuery = useQuery(
    [
      'leadManagementStageLeads',
      organizationId,
      propertyId,
      selectedStageKey,
      visibleCount,
    ],
    () =>
      service.getStageLeads(
        organizationId,
        propertyId,
        selectedStageKey,
        1,
        visibleCount,
      ),
    { enabled: enabled && Boolean(selectedStageKey), keepPreviousData: true },
  );

  const stages = useMemo(() => {
    const descriptors = stagesQuery.data ?? [];
    const countByKey = new Map(
      (countsQuery.data?.counts ?? []).map((entry) => [entry.key, entry.count]),
    );
    return descriptors.map((descriptor) => ({
      ...descriptor,
      count: countByKey.get(descriptor.key) ?? 0,
    }));
  }, [stagesQuery.data, countsQuery.data]);

  const groupedStages = useMemo(() => groupForInvertedDisplay(stages), [stages]);

  const maxCount = useMemo(
    () => stages.reduce((max, stage) => Math.max(max, stage.count), 0),
    [stages],
  );

  const totalActive = useMemo(
    () => stages.reduce((sum, stage) => sum + stage.count, 0),
    [stages],
  );

  // ENG-34128 — the two parked buckets shown below the funnel, each a "now" count
  // from the counts response. Not funnel stages; they lift leads out of the active
  // flow. Order matters: Waitlist above On Hold.
  const parkedBuckets = useMemo(() => {
    const parked = countsQuery.data?.parked ?? { waitlist: 0, onHold: 0 };
    return [
      { key: 'waitlist', count: parked.waitlist ?? 0 },
      { key: 'on-hold', count: parked.onHold ?? 0 },
    ];
  }, [countsQuery.data]);

  const selectedStage = useMemo(() => {
    const active = stages.find((stage) => stage.key === selectedStageKey);
    if (active) return active;
    // A parked bucket is selectable too; give the drill-down panel a label.
    const parked = parkedBuckets.find((bucket) => bucket.key === selectedStageKey);
    if (!parked) return null;
    const label =
      parked.key === 'waitlist'
        ? intl.formatMessage(messages.waitlistBucket)
        : intl.formatMessage(messages.onHoldBucket);
    return { key: parked.key, label, count: parked.count };
  }, [stages, parkedBuckets, selectedStageKey, intl]);

  const assigneeOptions = useMemo(
    () =>
      (prospectAssignees ?? []).map((u) => ({
        value: u.id,
        text: `${u.firstName} ${u.lastName}`,
      })),
    [prospectAssignees],
  );

  // Mapped list the pending-activity modal uses to build its completion-status
  // dropdown (edit-only here, so no "choose one" placeholder entry).
  const activityTypesList = useMemo(
    () =>
      (activityTypes ?? [])
        .filter((at) => at.automatedOnly === false)
        .map((activityType) => ({
          value: activityType.id,
          text: activityType.name,
          canBeOverlapped: activityType.canBeOverlapped,
          completionStatusOptions: activityType.completionStatusOptions,
          disabled: false,
        })),
    [activityTypes],
  );

  // Assigning can move a lead between stages (e.g. New Lead → Assigned) and
  // editing an activity can change last/next activity, so both refresh the
  // counts and the open stage list rather than patching a card in place.
  const refreshFunnel = () =>
    Promise.all([countsQuery.refetch(), leadsQuery.refetch()]);

  const onAssign = async (leadId, assigneeId) => {
    setAssigningLeadId(leadId);
    try {
      const prospectService = new ProspectProfileService();
      await prospectService.assign(
        { id: leadId, assignedToId: assigneeId, propertyId },
        organizationId,
      );
      toastr.success(
        intl.formatMessage(messages.assignSuccessBody),
        intl.formatMessage(messages.assignSuccessHeader),
        { showCloseButton: true, removeOnHover: true },
      );
      await refreshFunnel();
    } catch (err) {
      toastr.error(
        err.toString(),
        intl.formatMessage(messages.assignErrorHeader),
        { showCloseButton: true, removeOnHover: true },
      );
    } finally {
      setAssigningLeadId(null);
    }
  };

  const updateProspectActivity = async (activity) => {
    try {
      const activityService = new ActivityService();
      const payload = omit(activity, [
        'id',
        'saveAndClose',
        'saveAndAddNew',
        'createdAt',
        'updatedAt',
        'createdById',
        'updatedById',
        'updatedBy',
        'deletedAt',
        'lastUpdatedById',
        'ownedBy',
        'prospect',
        'completionStatus',
        'activityType',
      ]);
      await activityService.edit(payload, activity.id, organizationId);
      toastr.success(
        intl.formatMessage(messages.activitySavedBody),
        intl.formatMessage(messages.activitySavedHeader),
        { showCloseButton: true, removeOnHover: true },
      );
      await refreshFunnel();
    } catch (err) {
      toastr.error(err.toString(), intl.formatMessage(messages.activityError), {
        showCloseButton: true,
        removeOnHover: true,
      });
    }
  };

  const deleteProspectActivity = async (activity) => {
    try {
      const activityService = new ActivityService();
      await activityService.delete(omit(activity, ['prospect']), organizationId);
      toastr.success(
        intl.formatMessage(messages.activityDeletedBody),
        intl.formatMessage(messages.activitySavedHeader),
        { showCloseButton: true, removeOnHover: true },
      );
      await refreshFunnel();
    } catch (err) {
      toastr.error(err.toString(), intl.formatMessage(messages.activityError), {
        showCloseButton: true,
        removeOnHover: true,
      });
    }
  };

  // Open the edit-activity modal for a lead's last/next activity. Completed
  // activities (those with a completion status) open the read-mostly modal;
  // pending ones open the scheduler. Mirrors the prospects table's handler.
  const openActivity = (activity, lead) => {
    if (!activity) return;

    const prospect = {
      id: lead.leadId,
      firstName: lead.firstName,
      lastName: lead.lastName,
      createdAt: lead.prospectCreatedAt,
    };

    const activityType = (activityTypes ?? []).find(
      (at) => at.id === activity.activityTypeId,
    );
    const hydrated = {
      ...activity,
      activityType,
      completionStatus: activityType
        ? (activityType.completionStatusOptions ?? []).find(
            (status) => status.id === activity.activityCompletionStatusId,
          )
        : undefined,
      prospect,
    };

    const stageInfo = { currentStage: 'prospect', urlId: lead.leadId };

    const editPromise = hydrated.activityCompletionStatusId
      ? editCompletedActivity(store, intl, hydrated, stageInfo)
      : editPendingActivity(
          store,
          intl,
          hydrated,
          activityTypesList,
          assigneeOptions,
          hydrated.prospect,
          stageInfo,
        );

    editPromise
      .then((data) => {
        if (data.saveAndClose || data.saveAndAddNew) {
          updateProspectActivity(data);
        } else {
          deleteProspectActivity(data);
        }
      })
      // Dismissing the modal rejects the confirmation promise; that's a no-op.
      .catch(() => {});
  };

  return {
    documentTitle: intl.formatMessage(messages.pageTitle, { propertyName }),
    header: intl.formatMessage(messages.header),
    isPropertySelected: enabled,
    funnel: {
      groupedStages,
      maxCount,
      totalActive,
      parked: parkedBuckets,
      isLoading: stagesQuery.isLoading || countsQuery.isLoading,
      isError: stagesQuery.isError || countsQuery.isError,
      isEmpty: !stagesQuery.isLoading && !countsQuery.isLoading && totalActive === 0,
    },
    selectedStageKey,
    selectedStage,
    onSelectStage: handleSelectStage,
    leads: {
      data: leadsQuery.data?.leads ?? [],
      total: leadsQuery.data?.total ?? 0,
      isLoading: leadsQuery.isLoading,
      isError: leadsQuery.isError,
      // isLoading covers the first fetch (no data yet); isFetching-while-not-
      // loading is a "load more" reveal, where the prior page stays on screen.
      isFetchingMore: leadsQuery.isFetching && !leadsQuery.isLoading,
      hasMore:
        (leadsQuery.data?.leads?.length ?? 0) < (leadsQuery.data?.total ?? 0),
      onLoadMore: () => setVisibleCount((count) => count + PAGE_SIZE),
    },
    assign: {
      options: assigneeOptions,
      canAssign,
      assigningLeadId,
      onAssign,
    },
    activityActions: {
      onOpen: openActivity,
      isDisabled: isActivityEditDisabled,
    },
  };
};
